import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Space } from 'antd';
import { LockOutlined, KeyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../api';

const ChangePasswordModal = ({ open, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });

      if (response.data?.success) {
        message.success(response.data.message || 'Password changed successfully!');
        form.resetFields();
        if (onSuccess) onSuccess();
        if (onCancel) onCancel();
      } else {
        message.error(response.data?.message || 'Failed to change password');
      }
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to change password. Please verify your current password.';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    if (onCancel) onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 6 }}>
          <div style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: '#e6f4ff',
            color: '#1677ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}>
            <KeyOutlined />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Change Password</div>
            <div style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>Enter your current password to set a new one</div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      centered
      width={440}
      bodyStyle={{ paddingTop: 16 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
      >
        <Form.Item
          label={<span style={{ fontWeight: 600, color: '#334155' }}>Current Password</span>}
          name="currentPassword"
          rules={[{ required: true, message: 'Please enter your current password' }]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Enter current password"
            size="large"
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 600, color: '#334155' }}>New Password</span>}
          name="newPassword"
          rules={[
            { required: true, message: 'Please enter a new password' },
            { min: 6, message: 'Password must be at least 6 characters' },
          ]}
        >
          <Input.Password
            prefix={<KeyOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Enter new password (min. 6 characters)"
            size="large"
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 600, color: '#334155' }}>Confirm New Password</span>}
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('The two passwords do not match!'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<CheckCircleOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Confirm new password"
            size="large"
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            size="large"
            style={{ borderRadius: 8, minWidth: 90 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            style={{
              borderRadius: 8,
              minWidth: 140,
              background: '#1677ff',
              fontWeight: 600,
            }}
          >
            Update Password
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
